import React, { useEffect, useRef } from 'react';
import type { DimensionValue } from 'react-native';
import { View, StyleSheet, Animated, ViewStyle, Easing, Platform } from 'react-native';
import { useTheme, BORDER_RADIUS } from '../../theme';

interface SkeletonProps {
  width?: DimensionValue;
  height?: number;
  borderRadius?: number;
  style?: ViewStyle;
  /** A flat block with no shimmer. The visual audit can't freeze an RN
   *  Animated loop, so a skeleton that can appear in a baseline scene has to
   *  be static (ReadinessMeter's loading mode does the same). */
  static?: boolean;
  testID?: string;
}

export function Skeleton({
  width = '100%',
  height = 16,
  borderRadius = BORDER_RADIUS.md,
  style,
  static: isStatic = false,
  testID,
}: SkeletonProps) {
  const { colors } = useTheme();
  const shimmerValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (isStatic) return;
    const animation = Animated.loop(
      Animated.timing(shimmerValue, {
        toValue: 1,
        duration: 1500,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== 'web',
      })
    );
    animation.start();

    return () => animation.stop();
  }, [shimmerValue, isStatic]);

  const translateX = shimmerValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-200, 200],
  });

  return (
    <View
      testID={testID}
      style={[
        styles.container,
        {
          width,
          height,
          borderRadius,
          backgroundColor: colors.skeletonBase,
        },
        style,
      ]}>
      {isStatic ? null : (
        <Animated.View
          style={[
            styles.shimmer,
            {
              backgroundColor: colors.skeletonHighlight,
              transform: [{ translateX }],
            },
          ]}
        />
      )}
    </View>
  );
}

// Preset skeleton shapes
export function SkeletonText({
  lines = 3,
  lastLineWidth = '60%' as DimensionValue,
}: {
  lines?: number;
  lastLineWidth?: DimensionValue;
}) {
  return (
    <View>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          width={index === lines - 1 ? lastLineWidth : '100%'}
          height={14}
          style={{ marginBottom: index < lines - 1 ? 8 : 0 }}
        />
      ))}
    </View>
  );
}

export function SkeletonAvatar({ size = 40 }: { size?: number }) {
  return <Skeleton width={size} height={size} borderRadius={size / 2} />;
}

export function SkeletonCard() {
  const { colors } = useTheme();

  return (
    <View
      style={[
        styles.card,
        {
          backgroundColor: colors.surfaceElevated,
          borderColor: colors.border,
        },
      ]}>
      <View style={styles.cardHeader}>
        <SkeletonAvatar />
        <View style={styles.cardHeaderText}>
          <Skeleton width={120} height={14} />
          <Skeleton width={80} height={12} style={{ marginTop: 6 }} />
        </View>
      </View>
      <Skeleton height={200} borderRadius={BORDER_RADIUS.md} style={{ marginVertical: 12 }} />
      <SkeletonText lines={2} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  shimmer: {
    width: 200,
    height: '100%',
    opacity: 0.5,
  },
  card: {
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    padding: 16,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardHeaderText: {
    marginLeft: 12,
    flex: 1,
  },
});
